// This class contains the necessary method of gathering enrollment form related plan data
// getFormMappings() would return an array of FoxIt fields with the corresponding value
import { FormMapping } from './FormMapping'
import type { ScenarioOutput } from '@precoa/calculator-sdk'
import { PaymentType, Term } from '@precoa/calculator-sdk'
import { DateTime } from 'luxon'

export default class EnrollmentForm extends FormMapping {
	private readonly enrollmentPaymentDetails: ScenarioOutput | undefined
	constructor(planHolderId: string, enrollmentPaymentDetails?: ScenarioOutput) {
		super(planHolderId)
		this.enrollmentPaymentDetails = enrollmentPaymentDetails
	}

	// Helper to gather payment type and frequency for Section 5's Available Plans and Payment Mode section
	getPaymentTermAndModeMappings() {
		const paymentTermLength = this.planHolder.insurance.term
		const paymentType = this.planHolder.insurance.paymentType
		return [
			this.getCheckBoxMapping(
				'paymentSinglePayCheck',
				paymentTermLength === Term.SinglePay
			),
			this.getCheckBoxMapping(
				'payment3YearPayCheck',
				paymentTermLength === Term.ThreeYear
			),
			this.getCheckBoxMapping(
				'payment5YearPayCheck',
				paymentTermLength === Term.FiveYear
			),
			this.getCheckBoxMapping(
				'payment10YearPayCheck',
				paymentTermLength === Term.TenYear
			),
			this.getCheckBoxMapping(
				'payment20YearPayCheck',
				paymentTermLength === Term.TwentyYear
			),
			{
				name: 'paymentAnnuityCheck',
				value: 'Off',
			},
			this.getCheckBoxMapping(
				'paymentModeSinglePayEFTFromCheckingCheck',
				paymentType === PaymentType.SP_EFT
			),
			this.getCheckBoxMapping(
				'paymentModeMultiPayEFTFromCheckingCheck',
				paymentType === PaymentType.MP_EFT
			),
			this.getCheckBoxMapping(
				'paymentModeOtherCheck',
				paymentType !== PaymentType.SP_EFT && paymentType !== PaymentType.MP_EFT
			),
			{
				name: 'paymentModePaymentDate',
				value: paymentType === PaymentType.MP_EFT ? '5' : '',
			},
		]
	}

	// Helper to gather singlePay/multiPay amount for Section 5's Face and Payment Information section
	getPaymentAmountMappings() {
		if (!this.enrollmentPaymentDetails) return []
		const paymentDetails = this.enrollmentPaymentDetails

		return [
			{
				name: 'singlePayFaceAmount',
				value: this.formatAmount(paymentDetails.singlePayFaceAmt),
			},
			{
				name: 'singlePayPaymentToday',
				value: this.formatAmount(paymentDetails.singlePayDueTodayAmt),
			},
			{
				name: 'multiPayFaceAmount',
				value: this.formatAmount(paymentDetails.multiPayFaceAmt),
			},
			{
				name: 'multiPayMonthlyPayment',
				value: this.formatAmount(paymentDetails.multiPayPeriodicAmt),
			},
			{
				name: 'multiPayPaymentToday',
				value: this.formatAmount(paymentDetails.multiPayDueTodayAmt),
			},
			{
				name: 'totalFaceAmount',
				value: this.formatAmount(paymentDetails.totalFaceAmt),
			},
			{
				name: 'totalPremiumAmt',
				value: this.formatAmount(paymentDetails.totalPaidAmt),
			},
			{
				name: 'totalFirstPayment',
				value: this.formatAmount(paymentDetails.totalDueTodayAmt),
			},
		]
	}

	getFormMappings() {
		const mappings = [
			...this.getMiddleNameInitialMapping(),
			...this.getProposedInsuredMapping(),
			...this.getPolicyOwnerAddressMapping(),
			...this.getPaymentTermAndModeMappings(),
			...this.getPaymentAmountMappings(),
			...this.getFuneralHomeMapping(),
			...this.getFuneralHomeRepresentativeMappings(),
		]
		if (!this.planHolder.ownerIsInsured) {
			mappings.push(...this.getPolicyOwnerMapping())
		}
		return mappings
	}
}
